import { useCallback, useEffect, useState } from "react";
import { Wrench, X, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_OPTIONS = [
    { id: "open", label: "Aberto" },
    { id: "in_progress", label: "Em curso" },
    { id: "scheduled", label: "Agendado" },
    { id: "resolved", label: "Resolvido" },
    { id: "closed", label: "Fechado" },
];
const STATUS_CLASS = {
    open: "bg-blue-100 text-blue-700 border-blue-200",
    in_progress: "bg-amber-100 text-amber-700 border-amber-200",
    scheduled: "bg-violet-100 text-violet-700 border-violet-200",
    resolved: "bg-emerald-100 text-emerald-700 border-emerald-200",
    closed: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export default function AdminServiceRequests() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [active, setActive] = useState(null); // request being edited
    const [responseDraft, setResponseDraft] = useState("");
    const [statusDraft, setStatusDraft] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const url = filter ? `/admin/service-requests?status_filter=${filter}` : `/admin/service-requests`;
            const { data } = await api.get(url);
            setItems(data);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao carregar.");
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    function open(it) {
        setActive(it);
        setResponseDraft(it.admin_response || "");
        setStatusDraft(it.status);
    }

    async function save() {
        try {
            await api.put(`/admin/service-requests/${active.id}`, {
                status: statusDraft,
                admin_response: responseDraft || null,
            });
            toast.success("Pedido actualizado");
            setActive(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao guardar.");
        }
    }

    async function del(id) {
        if (!confirm("Eliminar este pedido?")) return;
        try {
            await api.delete(`/admin/service-requests/${id}`);
            toast.success("Eliminado");
            if (active?.id === id) setActive(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao eliminar.");
        }
    }

    return (
        <div data-testid="admin-sr-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="font-heading text-2xl font-extrabold text-primary flex items-center gap-2">
                        <Wrench className="h-6 w-6" /> Pedidos de assistência
                    </h2>
                    <p className="text-sm text-muted-foreground">Garantias, manutenção e avarias submetidas pelos clientes.</p>
                </div>
                <select value={filter} onChange={(e) => setFilter(e.target.value)}
                    data-testid="sr-filter"
                    className="rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary">
                    <option value="">Todos os estados</option>
                    {STATUS_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
            </div>

            <div className="mt-6 rounded-2xl border border-border bg-white overflow-x-auto">
                {loading ? (
                    <div className="p-6 sm:p-8 text-sm text-muted-foreground">A carregar...</div>
                ) : items.length === 0 ? (
                    <div className="p-6 sm:p-8 text-sm text-muted-foreground text-center">Sem pedidos.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="sr-table">
                        <thead className="bg-muted text-[11px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="text-left px-4 py-3">Cliente</th>
                                <th className="text-left px-4 py-3">Tipo</th>
                                <th className="text-left px-4 py-3">Assunto</th>
                                <th className="text-left px-4 py-3">Estado</th>
                                <th className="text-left px-4 py-3">Data</th>
                                <th className="text-right px-4 py-3">Acções</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {items.map((it) => (
                                <tr key={it.id} data-testid={`sr-row-${it.id}`} className="hover:bg-muted/50">
                                    <td className="px-4 py-3">
                                        <div className="font-bold text-primary">{it.user_name || "—"}</div>
                                        <div className="text-xs text-muted-foreground">{it.user_email}</div>
                                    </td>
                                    <td className="px-4 py-3 uppercase text-xs font-bold">{it.type}</td>
                                    <td className="px-4 py-3">
                                        <div className="font-bold text-primary">{it.title}</div>
                                        <div className="text-xs text-muted-foreground line-clamp-1">{it.description}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLASS[it.status]}`}>
                                            {STATUS_OPTIONS.find((s) => s.id === it.status)?.label || it.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs">{fmt(it.created_at)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => open(it)} data-testid={`sr-open-${it.id}`}
                                            className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted">Abrir</button>
                                        <button onClick={() => del(it.id)} data-testid={`sr-delete-${it.id}`}
                                            className="ml-2 inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100">
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {active && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setActive(null)}>
                    <div onClick={(e) => e.stopPropagation()}
                        data-testid="sr-detail"
                        className="w-full max-w-2xl rounded-2xl bg-white p-6 lg:p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="font-heading text-xl font-extrabold text-primary">{active.title}</h3>
                                <p className="text-xs text-muted-foreground">{active.user_name} · {active.user_email}</p>
                            </div>
                            <button onClick={() => setActive(null)}><X className="h-5 w-5" /></button>
                        </div>
                        <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Descrição do cliente</div>
                        <p className="mt-2 rounded-md bg-muted p-3 text-sm whitespace-pre-line text-primary">{active.description}</p>

                        <div className="mt-5">
                            <label className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Estado</label>
                            <select value={statusDraft} onChange={(e) => setStatusDraft(e.target.value)}
                                data-testid="sr-status"
                                className="mt-2 w-full rounded-md border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:border-primary focus:bg-white">
                                {STATUS_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                            </select>
                        </div>
                        <div className="mt-4">
                            <label className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Resposta NOVIFOOD (visível ao cliente)</label>
                            <textarea value={responseDraft} onChange={(e) => setResponseDraft(e.target.value)} rows={5}
                                data-testid="sr-response"
                                className="mt-2 w-full rounded-md border border-border bg-muted px-3 py-3 text-sm outline-none focus:border-primary focus:bg-white"
                                placeholder="Ex: Visita técnica agendada para..." />
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button type="button" onClick={() => setActive(null)}
                                className="rounded-md border border-border px-4 py-2 text-sm font-bold">Cancelar</button>
                            <button onClick={save} data-testid="sr-save"
                                className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-bold hover:-translate-y-0.5 transition">
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function fmt(s) {
    if (!s) return "—";
    try {
        const d = typeof s === "string" ? new Date(s) : s;
        return d.toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return String(s); }
}
